import { type IconProps } from "@tokenterminal/core/ui/icon/Icon"
import { type Interval } from "@tokenterminal/tt-analytics-api-types/dist/api/zoom"
import { useIsMobile } from "@tokenterminal/ui/hooks/isMobile"
import useMarketSectorsList from "modules/projects/EcosystemTab/useMarketSectorsList"
import { useMetricBreakdown } from "modules/projects/hooks/useMetricBreakdowns"
import useMetricsConfigurationBySlug from "modules/projects/OverviewTab/useMetricsConfigurationBySlug"
import { useMemo, useRef } from "react"
import { iconsBySlug } from "../constants"
import { toSlug } from "../utils"

/**
 * Splits an array into two arrays based on a condition function. If the
 * condition is true, the element is added to the first array, otherwise it is
 * added to the second array.
 */

function splitArray<T>(array: T[], condition: (item: T) => boolean) {
  return array.reduce<[T[], T[]]>(
    ([array1, array2], elem) => {
      return condition(elem)
        ? [[...array1, elem], array2]
        : [array1, [...array2, elem]]
    },
    [[], []]
  )
}

/**
 * Adjusts the values of treemap items to improve usability of click targets
 * (larger items are easier to click). Generally preserves relative size between
 * items.
 */

function adjustItemsForClickTargetSize(items: TreemapItem[], power = 0.8) {
  items.forEach((item) => {
    item.treemapValue = Math.pow(item.originalValue, power)
  })
  const totalOfTreemapValues = items.reduce(
    (acc, item) => acc + item.treemapValue,
    0
  )
  items.forEach((item) => {
    item.treemapValue = item.treemapValue / totalOfTreemapValues
  })
}

type BaseTreemapItem = {
  name: string
  percentageOfTotal: number
  originalValue: number
  treemapValue: number
}

type RegularTreemapItem = BaseTreemapItem & {
  type: "regular"
  change: number
  icon: IconProps["as"]
  slug: string
}

export type OtherGroupTreemapItem = BaseTreemapItem & {
  type: "other"
  otherGroupCount: number
  otherChildren: TreemapItem[]
}

export type TreemapItem = RegularTreemapItem | OtherGroupTreemapItem

export function isOtherGroupItem(
  item: TreemapItem
): item is OtherGroupTreemapItem {
  return item.type === "other"
}

export const useTreemapData = ({
  selectedMetricId,
  interval,
}: {
  selectedMetricId: string
  interval: Interval
}): {
  data: TreemapItem[] | undefined
  isLoading: boolean
} => {
  const isMobile = useIsMobile()
  const prevData = useRef<TreemapItem[] | undefined>(undefined)
  const metricConfiguration = useMetricsConfigurationBySlug(
    toSlug(selectedMetricId)
  )
  const { data: marketSectors = [], isFetching: marketSectorsFetching } =
    useMarketSectorsList()
  const {
    data: marketCapBreakdownResult,
    isFetching: marketCapBreakdownFetching,
  } = useMetricBreakdown({
    groupBy: "market-sector",
    metricIds: [selectedMetricId],
    interval,
  })

  const marketCapBreakdownDataBySectorSlug = useMemo(() => {
    const marketCapBreakdownData = marketCapBreakdownResult?.data || []

    return marketCapBreakdownData?.reduce(
      (acc, item) => {
        const slug = toSlug(item.market_sector_id || "")
        const itemData = item.metrics[selectedMetricId]
        const value = metricConfiguration?.static
          ? itemData?.latest || 0
          : itemData?.sum || 0

        acc[slug] = {
          value,
          change: itemData?.change || 0,
        }
        return acc
      },
      {} as Record<string, { value: number; change: number }>
    )
  }, [marketCapBreakdownResult, selectedMetricId, metricConfiguration])

  const totalValue = useMemo(() => {
    return Object.values(marketCapBreakdownDataBySectorSlug).reduce(
      (acc, item) => acc + item.value,
      0
    )
  }, [marketCapBreakdownDataBySectorSlug])

  const sortedTreemapItems = useMemo(() => {
    const baseItems = marketSectors
      ?.map((item) => ({
        type: "regular" as const,
        name: item.name,
        originalValue:
          marketCapBreakdownDataBySectorSlug[item.slug]?.value || 0,
        change: marketCapBreakdownDataBySectorSlug[item.slug]?.change || 0,
        percentageOfTotal:
          (marketCapBreakdownDataBySectorSlug[item.slug]?.value || 0) /
          totalValue,
        slug: item.slug,
        icon: iconsBySlug[item.slug],
        treemapValue: marketCapBreakdownDataBySectorSlug[item.slug]?.value || 0,
      }))
      .sort((a, b) => b.originalValue - a.originalValue)
      .filter((item) => item.originalValue > 0)

    // Fallback to stale data if fetching
    if (marketSectorsFetching || marketCapBreakdownFetching) {
      return prevData.current
    } else if (baseItems.length === 0) {
      return []
    }

    // Group items with less than a certain % market share into "Other" category
    // so they're large enough to interact with
    const desktopThreshold = 0.2 / 100 // 0.2%
    const mobileThreshold = 5 / 100 // 5%

    const [itemsToShow, itemsToHide] = splitArray(
      baseItems,
      (item) =>
        item.percentageOfTotal >=
        (isMobile ? mobileThreshold : desktopThreshold)
    )

    const otherGroupItem: OtherGroupTreemapItem = {
      type: "other" as const,
      name: "Other",
      originalValue: itemsToHide.reduce(
        (acc, item) => acc + item.originalValue,
        0
      ),
      treemapValue: itemsToHide.reduce(
        (acc, item) => acc + item.treemapValue,
        0
      ),
      percentageOfTotal: itemsToHide.reduce(
        (acc, item) => acc + item.percentageOfTotal,
        0
      ),
      otherGroupCount: itemsToHide.length,
      otherChildren: itemsToHide,
    }

    const combinedItems = [...itemsToShow, otherGroupItem]

    adjustItemsForClickTargetSize(combinedItems)

    prevData.current = combinedItems
    return combinedItems
  }, [
    marketSectors,
    marketSectorsFetching,
    marketCapBreakdownFetching,
    marketCapBreakdownDataBySectorSlug,
    totalValue,
  ])

  return useMemo(() => {
    return {
      data: sortedTreemapItems,
      isLoading: marketSectorsFetching || marketCapBreakdownFetching,
    }
  }, [sortedTreemapItems, marketSectorsFetching, marketCapBreakdownFetching])
}
